import { z } from "zod";

export const apiEnvelopeSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: dataSchema.nullable(),
    error: z.string().nullable().optional(),
    meta: z.record(z.any()).optional(),
  });

export const searchParamSchema = z.object({
  symbol: z.string().default("000001.SZ"),
  interval: z.enum(["1", "5", "15", "30", "60", "D", "W", "M"]).default("D"),
  adjust: z.enum(["none", "qfq", "hfq"]).default("none"),
  indicators: z.array(z.string()).default(["ma"]),
  watchlistId: z.number().nullable().default(null),
  leftPanel: z.enum(["expanded", "collapsed"]).default("expanded"),
  rightPanel: z.enum(["expanded", "collapsed"]).default("expanded"),
  layoutPreset: z.string().default("terminal"),
});

export const wyckoffSearchParamSchema = z.object({
  symbol: z.string().default("000001.SZ"),
  interval: z.enum(["D", "W", "M"]).default("D"),
  adjust: z.enum(["none", "qfq", "hfq"]).default("qfq"),
  watchlistId: z.number().nullable().default(null),
  limit: z.number().int().min(120).max(2000).default(420),
  leftPanel: z.enum(["expanded", "collapsed"]).default("expanded"),
  rightPanel: z.enum(["expanded", "collapsed"]).default("expanded"),
  layoutPreset: z.string().default("wyckoff-terminal"),
});

export const watchlistSchema = z.object({
  id: z.number(),
  user_id: z.number().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  item_count: z.number().optional(),
  position: z.number().optional(),
  sort_by: z.string().optional(),
  sort_dir: z.enum(["asc", "desc"]).optional(),
});

export const watchlistItemSchema = z.object({
  id: z.number(),
  watchlist_id: z.number().optional(),
  instrument_type: z.string().optional(),
  symbol: z.string(),
  market: z.string().nullable().optional(),
  name_cache: z.string().nullable().optional(),
  display_name: z.string().nullable().optional(),
  stock_name: z.string().nullable().optional(),
  alias: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  sort_order: z.number().optional(),
  pinned: z.boolean().optional(),
  row_color: z.string().nullable().optional(),
  close: z.number().nullable().optional(),
  pct_chg: z.number().nullable().optional(),
  pre_close: z.number().nullable().optional(),
  change: z.number().nullable().optional(),
  amount: z.number().nullable().optional(),
  vol: z.number().nullable().optional(),
  trade_date: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export const taskRunSchema = z.object({
  id: z.number().or(z.string()),
  status: z.string().optional(),
  run_type: z.string().optional(),
  task_key: z.string().optional(),
  progress_pct: z.number().nullable().optional(),
  summary_text: z.string().nullable().optional(),
  started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(),
});

export const bootstrapSchema = z.object({
  current_user: z.any().nullable(),
  permissions: z.object({
    authenticated: z.boolean(),
    can_manage_tasks: z.boolean(),
  }),
  default_symbol: z.string(),
  default_interval: z.string(),
  default_adjust: z.string(),
  default_indicators: z.array(z.string()),
  watchlists: z.object({
    profile: z.any().nullable(),
    default_watchlist_id: z.number().nullable(),
    watchlists: z.array(watchlistSchema),
    max_lists_per_user: z.number(),
    max_items_per_list: z.number(),
  }),
  layout: z.object({
    left_panel: z.string(),
    right_panel: z.string(),
    preset: z.string(),
  }),
  task_summary: z.object({
    datasync_runs: z.array(taskRunSchema),
    quant_runs: z.array(taskRunSchema),
    can_manage: z.boolean(),
  }),
  features: z.object({
    chart_engine: z.enum(["lightweight", "tradingview"]),
    tradingview_ready: z.boolean(),
    echarts_ready: z.boolean(),
    monaco_ready: z.boolean(),
  }),
});

export const searchResultSchema = z.object({
  symbol: z.string().optional(),
  ts_code: z.string().optional(),
  name: z.string().optional(),
});

export const quoteItemSchema = z
  .object({
    time: z.string().nullable().optional(),
    close: z.number().nullable().optional(),
    pct_chg: z.number().nullable().optional(),
    pre_close: z.number().nullable().optional(),
    change: z.number().nullable().optional(),
    open: z.number().nullable().optional(),
    high: z.number().nullable().optional(),
    low: z.number().nullable().optional(),
    vol: z.number().nullable().optional(),
    volume: z.number().nullable().optional(),
    amount: z.number().nullable().optional(),
    source: z.string().nullable().optional(),
  })
  .passthrough();

export const quoteSchema = z.object({
  trade_date: z.string().optional().default(""),
  in_trading_session: z.boolean().optional().default(false),
  in_trading_day: z.boolean().optional().default(false),
  live_sync_window: z.boolean().optional().default(false),
  source: z.string().optional().default("db_fallback"),
  quotes: z.record(z.string(), quoteItemSchema).optional().default({}),
  errors: z.record(z.string(), z.string()).optional().default({}),
});

export const barsSchema = z.array(z.any());

export const tasksSummarySchema = z.object({
  datasync_runs: z.array(taskRunSchema),
  quant_runs: z.array(taskRunSchema),
  can_manage: z.boolean().optional(),
});

export const wyckoffRowSchema = z.object({
  time: z.string(),
  phase: z.string(),
  phase_name: z.string().optional(),
  strength: z.number().nullable().optional(),
  confidence: z.number().nullable().optional(),
  pos: z.number().nullable().optional(),
  atr: z.number().nullable().optional(),
  tr_high: z.number().nullable().optional(),
  tr_low: z.number().nullable().optional(),
  acc_score: z.number().nullable().optional(),
  dist_score: z.number().nullable().optional(),
  spring: z.boolean().optional(),
  upthrust: z.boolean().optional(),
  sos: z.boolean().optional(),
  sow: z.boolean().optional(),
});

export const wyckoffSegmentSchema = z.object({
  phase: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  start_index: z.number(),
  end_index: z.number(),
  top: z.number(),
  bottom: z.number(),
  confidence: z.number().nullable().optional(),
  strength: z.number().nullable().optional(),
  bars: z.number().optional(),
});

export const wyckoffAnalysisSchema = z.object({
  rows: z.array(wyckoffRowSchema),
  segments: z.array(wyckoffSegmentSchema).default([]),
  summary: z
    .object({
      phase: z.string(),
      phase_name: z.string().optional(),
      strength: z.number().nullable().optional(),
      confidence: z.number().nullable().optional(),
      pos: z.number().nullable().optional(),
      atr_pct: z.number().nullable().optional(),
      event_counts: z
        .object({
          spring: z.number().optional().default(0),
          upthrust: z.number().optional().default(0),
          sos: z.number().optional().default(0),
          sow: z.number().optional().default(0),
        })
        .optional(),
    })
    .optional(),
});
